import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import Drawer from "../../layouts/Drawer";
import { IInboxApiTemplate, IInboxSettings, IInboxTemplateRequest } from "../../models/messagesInbox/IInboxApi";
import { IInboxConversation, InboxType } from "../../models/messagesInbox/IInboxConversation";
import classNames from "../../utils/classNames";
import { fillMergeFields } from "../../utils/fillMergeFields";
import { IInboxNotice, InboxNotice, useInboxLive } from "./InboxBits";
import { TYPE_LABELS } from "./ThreadView";

type Tab = "signature" | "templates" | "ai" | "auto";

/** The merge fields a template or automatic request may use, in the order the help text lists them. */
const MERGE_FIELDS = ["buyer_first_name", "part_title", "order_no", "courier", "tracking_no", "est_delivery", "shop_name", "sign_off"];
const OUT_OF_HOURS_FIELDS = ["buyer_first_name", "working_hours", "next_open", "shop_name", "sign_off"];
const DAYS: { key: string; label: string }[] = [
  { key: "mon", label: "Mon" },
  { key: "tue", label: "Tue" },
  { key: "wed", label: "Wed" },
  { key: "thu", label: "Thu" },
  { key: "fri", label: "Fri" },
  { key: "sat", label: "Sat" },
  { key: "sun", label: "Sun" },
];

/** "Mon to Fri, 08:00 to 17:00", as the API fills {working_hours}; blank without hours. */
export function workingHoursText(s: Pick<IInboxSettings, "workingHoursStart" | "workingHoursEnd" | "workingDays">) {
  if (!s.workingHoursStart || !s.workingHoursEnd) return "";
  const picked = DAYS.filter((d) => s.workingDays.split(",").map((x) => x.trim().toLowerCase()).includes(d.key));
  const consecutive = picked.length > 2 && DAYS.indexOf(picked[picked.length - 1]) - DAYS.indexOf(picked[0]) === picked.length - 1;
  const days = picked.length === 0 ? "" : consecutive ? `${picked[0].label} to ${picked[picked.length - 1].label}` : picked.map((d) => d.label).join(", ");
  return `${days ? `${days}, ` : ""}${s.workingHoursStart} to ${s.workingHoursEnd}`;
}

function toggleDay(days: string, key: string) {
  const set = new Set(days.split(",").map((x) => x.trim().toLowerCase()).filter(Boolean));
  if (set.has(key)) set.delete(key);
  else set.add(key);
  return DAYS.map((d) => d.key).filter((k) => set.has(k)).join(",");
}

interface Props {
  open: boolean;
  onClose: () => void;
  settings?: IInboxSettings;
  settingsError?: string;
  templates: IInboxApiTemplate[];
  /** Merge values for the open conversation (or a sample one), for the previews. */
  previewValues: Record<string, string | null | undefined>;
  previewConversation?: IInboxConversation;
  saving: boolean;
  onSaveSettings: (dto: IInboxSettings) => Promise<unknown>;
  onAddTemplate: (req: IInboxTemplateRequest) => Promise<unknown>;
  onUpdateTemplate: (id: string, req: IInboxTemplateRequest) => Promise<unknown>;
  onDeleteTemplate: (id: string) => Promise<unknown>;
}

const field = "w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-[13px] focus:border-primary-500 focus:ring-primary-500";
const label = "block text-[12px] font-medium text-gray-700";
const help = "mt-1 text-[12px] text-gray-500";
const primary = "rounded-lg bg-primary-600 px-3 py-1.5 text-[13px] font-medium text-white hover:bg-primary-700 disabled:opacity-60";
const secondary = "rounded-lg border border-gray-200 px-3 py-1.5 text-[13px] text-gray-700 hover:bg-gray-50 disabled:opacity-60";

function errorText(error: unknown, fallback: string) {
  return error instanceof Error && error.message ? error.message : fallback;
}

function MergeHelp({ fields = MERGE_FIELDS }: { fields?: string[] }) {
  const { t } = useTranslation("messages");
  return (
    <p className={help}>
      {t("Merge fields:")} {fields.map((f) => `{${f}}`).join(", ")}
    </p>
  );
}

function Preview({ text, values }: { text: string; values: Record<string, string | null | undefined> }) {
  const { t } = useTranslation("messages");
  return (
    <div className="mt-2 rounded-lg border border-dashed border-gray-200 bg-gray-50 px-3 py-2">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{t("Preview")}</p>
      <p className="mt-1 whitespace-pre-wrap text-[13px] text-gray-800">{fillMergeFields(text, values)}</p>
    </div>
  );
}

/**
 * The settings behind the cog: how replies sign off, the composer's templates, the AI switch and
 * context, and the automatic reg / address requests. Everyone with the inbox gets the first two;
 * the last two only show for admins (the API refuses them for anyone else too).
 */
export default function InboxSettingsDrawer({ open, onClose, settings, settingsError, templates, previewValues, previewConversation, saving, onSaveSettings, onAddTemplate, onUpdateTemplate, onDeleteTemplate }: Props) {
  const { t } = useTranslation("messages");
  const live = useInboxLive();
  const [tab, setTab] = useState<Tab>("signature");
  const [draft, setDraft] = useState<IInboxSettings | undefined>(settings);
  const [notice, setNotice] = useState<IInboxNotice>();

  useEffect(() => {
    setDraft(settings);
  }, [settings, open]);

  useEffect(() => {
    setNotice(undefined);
  }, [tab, open]);

  const tabs: { id: Tab; label: string }[] = [
    { id: "signature", label: t("Signature") },
    { id: "templates", label: t("Templates") },
    ...(settings?.canEditAi && settings.aiAvailable ? [{ id: "ai" as Tab, label: t("AI") }] : []),
    ...(settings?.canEditAi ? [{ id: "auto" as Tab, label: t("Automatic replies") }] : []),
  ];

  const change = <K extends keyof IInboxSettings>(key: K, value: IInboxSettings[K]) => setDraft((d) => (d ? { ...d, [key]: value } : d));

  const save = async () => {
    if (!draft) return;
    try {
      await onSaveSettings(draft);
      setNotice({ tone: "green", text: t("Saved.") });
    } catch (error) {
      setNotice({ tone: "red", text: errorText(error, t("Couldn't save – try again.")) });
    }
  };

  const setOpen = (value: boolean | ((prev: boolean) => boolean)) => {
    const next = typeof value === "function" ? value(open) : value;
    if (!next) onClose();
  };

  const values = {
    ...previewValues,
    sign_off: draft ? fillMergeFields(draft.signOff, { shop_name: draft.shopName || previewValues.shop_name }) : previewValues.sign_off,
    working_hours: (draft && workingHoursText(draft)) || t("Mon to Fri, 08:00 to 17:00"),
    next_open: t("Monday at {{time}}", { time: draft?.workingHoursStart || "08:00" }),
  };

  return (
    <Drawer open={open} setOpen={setOpen} title={t("Messages settings")} maxWidth="max-w-xl">
      {!live && <InboxNotice notice={{ tone: "amber", text: t("Sample data: settings aren't saved anywhere in this preview.") }} className="mb-3" />}
      {settingsError && <InboxNotice notice={{ tone: "red", text: settingsError }} className="mb-3" />}

      <div className="mb-4 flex gap-1 border-b border-gray-200">
        {tabs.map((x) => (
          <button
            key={x.id}
            type="button"
            onClick={() => setTab(x.id)}
            className={classNames("-mb-px border-b-2 px-3 py-2 text-[13px] font-medium", tab === x.id ? "border-primary-600 text-primary-700" : "border-transparent text-gray-500 hover:text-gray-700")}
          >
            {x.label}
          </button>
        ))}
      </div>

      {notice && <InboxNotice notice={notice} className="mb-3" />}

      {tab === "signature" && draft && (
        <div className="space-y-4">
          <div>
            <label className={label}>{t("Sign replies as")}</label>
            <input value={draft.shopName} onChange={(e) => change("shopName", e.target.value)} placeholder={previewValues.shop_name ?? ""} className={field} />
            <p className={help}>{t("What {shop_name} fills in as. Blank uses your business name from Wilma.", { shop_name: "{shop_name}" })}</p>
          </div>
          <div>
            <label className={label}>{t("Sign-off")}</label>
            <textarea value={draft.signOff} onChange={(e) => change("signOff", e.target.value)} rows={3} className={field} />
            <p className={help}>{t("Goes at the end of every drafted and automatic reply.")}</p>
            <Preview text={`Hi {buyer_first_name}, thanks for your message.\n\n{sign_off}`} values={values} />
          </div>
          <div>
            <label className={label}>{t("Reply within (hours)")}</label>
            <input type="number" min={1} max={168} value={draft.replyWindowHours} onChange={(e) => change("replyWindowHours", Number(e.target.value))} className={classNames(field, "w-28")} />
            <p className={help}>{t("The countdown on each conversation runs from the buyer's first unanswered message.")}</p>
          </div>
          <button type="button" onClick={save} disabled={saving} className={primary}>
            {t("Save")}
          </button>
        </div>
      )}

      {tab === "templates" && <TemplatesTab templates={templates} values={values} saving={saving} onAdd={onAddTemplate} onUpdate={onUpdateTemplate} onDelete={onDeleteTemplate} setNotice={setNotice} />}

      {tab === "ai" && draft && (
        <div className="space-y-4">
          <label className="flex items-start gap-2">
            <input type="checkbox" checked={draft.aiEnabled} onChange={(e) => change("aiEnabled", e.target.checked)} className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
            <span>
              <span className="block text-[13px] font-medium text-gray-900">{t("Read new messages with AI")}</span>
              <span className="block text-[12px] text-gray-500">{t("Works out what each buyer wants (cancellation, address change, fitment…), writes a one-line summary and checks whether they've given their reg or address. Nothing is sent.")}</span>
            </span>
          </label>
          <div>
            <label className={label}>{t("About your business")}</label>
            <textarea value={draft.aiBusinessContext} onChange={(e) => change("aiBusinessContext", e.target.value)} rows={3} className={field} placeholder={t("e.g. We only sell used parts, no new. Collection from the yard is fine.")} />
            <p className={help}>{t("A line or two the AI reads before each message, so its summaries fit how you work.")}</p>
          </div>
          <button type="button" onClick={save} disabled={saving} className={primary}>
            {t("Save")}
          </button>
        </div>
      )}

      {tab === "auto" && draft && (
        <div className="space-y-5">
          <div>
            <label className={label}>{t("When a buyer hasn't given their reg or new address")}</label>
            {!draft.aiAvailable && <p className={help}>{t("Needs Messages AI, which isn't switched on for you yet. The out-of-hours reply below works without it.")}</p>}
            <div className="mt-1 space-y-1.5">
              {(["off", "draft", "send"] as const).map((mode) => (
                <label key={mode} className="flex items-start gap-2">
                  <input type="radio" name="autoReplyMode" checked={draft.autoReplyMode === mode} onChange={() => change("autoReplyMode", mode)} className="mt-0.5 border-gray-300 text-primary-600 focus:ring-primary-500" />
                  <span>
                    <span className="block text-[13px] font-medium text-gray-900">
                      {mode === "off" ? t("Do nothing") : mode === "draft" ? t("Draft the request for someone to send") : t("Send automatically")}
                    </span>
                    {mode === "draft" && <span className="block text-[12px] text-gray-500">{t("The fitment and address cards draft it in your wording below, so you can see exactly what sending would post.")}</span>}
                    {mode === "send" && <span className="block text-[12px] text-gray-500">{t("Only when AI is sure of the question, nobody has replied, and never twice on one conversation. Also switches on the out-of-hours reply.")}</span>}
                  </span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-[13px] text-gray-800">
              <input type="checkbox" checked={draft.autoReplyEbay} onChange={(e) => change("autoReplyEbay", e.target.checked)} className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" /> eBay
            </label>
            <label className="flex items-center gap-2 text-[13px] text-gray-800">
              <input type="checkbox" checked={draft.autoReplyPartPro} onChange={(e) => change("autoReplyPartPro", e.target.checked)} className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" /> PartPro
            </label>
          </div>
          {draft.aiAvailable && (
            <>
              <div>
                <label className={label}>{t("Reg / VIN request")}</label>
                <textarea value={draft.autoRegRequestBody} onChange={(e) => change("autoRegRequestBody", e.target.value)} rows={4} className={field} />
                <MergeHelp />
                <Preview text={draft.autoRegRequestBody} values={values} />
              </div>
              <div>
                <label className={label}>{t("New address request")}</label>
                <textarea value={draft.autoAddressRequestBody} onChange={(e) => change("autoAddressRequestBody", e.target.value)} rows={4} className={field} />
                <Preview text={draft.autoAddressRequestBody} values={values} />
              </div>
            </>
          )}
          <div>
            <label className={label}>{t("After Wilma replies by itself")}</label>
            <div className="mt-1 space-y-1.5">
              <label className="flex items-start gap-2">
                <input type="radio" name="autoRepliesAnswer" checked={!draft.autoRepliesAnswer} onChange={() => change("autoRepliesAnswer", false)} className="mt-0.5 border-gray-300 text-primary-600 focus:ring-primary-500" />
                <span>
                  <span className="block text-[13px] font-medium text-gray-900">{t("Keep it in Needs reply")}</span>
                  <span className="block text-[12px] text-gray-500">{t("Someone checks the automatic reply and clicks No reply needed if it did the job. Recommended while you're judging them.")}</span>
                </span>
              </label>
              <label className="flex items-start gap-2">
                <input type="radio" name="autoRepliesAnswer" checked={draft.autoRepliesAnswer} onChange={() => change("autoRepliesAnswer", true)} className="mt-0.5 border-gray-300 text-primary-600 focus:ring-primary-500" />
                <span>
                  <span className="block text-[13px] font-medium text-gray-900">{t("Count it as answered")}</span>
                  <span className="block text-[12px] text-gray-500">{t("It leaves the queue until the buyer writes back.")}</span>
                </span>
              </label>
            </div>
          </div>
          <div>
            <label className={label}>{t("Working hours (London time)")}</label>
            <div className="mt-1 flex items-center gap-2">
              <input type="time" value={draft.workingHoursStart} onChange={(e) => change("workingHoursStart", e.target.value)} className={classNames(field, "w-28")} />
              <span className="text-[13px] text-gray-500">{t("to")}</span>
              <input type="time" value={draft.workingHoursEnd} onChange={(e) => change("workingHoursEnd", e.target.value)} className={classNames(field, "w-28")} />
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {DAYS.map((d) => {
                const on = draft.workingDays.split(",").map((x) => x.trim().toLowerCase()).includes(d.key);
                return (
                  <button
                    key={d.key}
                    type="button"
                    onClick={() => change("workingDays", toggleDay(draft.workingDays, d.key))}
                    className={classNames("rounded-full border px-2.5 py-0.5 text-[12px] font-medium", on ? "border-primary-600 bg-primary-50 text-primary-700" : "border-gray-200 text-gray-500 hover:bg-gray-50")}
                  >
                    {t(d.label)}
                  </button>
                );
              })}
            </div>
            <p className={help}>{t("Reg and address requests go at any time. Both times blank means always open.")}</p>
          </div>
          <div>
            <label className="flex items-start gap-2">
              <input type="checkbox" checked={draft.outOfHoursReplyEnabled} onChange={(e) => change("outOfHoursReplyEnabled", e.target.checked)} className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
              <span>
                <span className="block text-[13px] font-medium text-gray-900">{t("Out-of-hours reply")}</span>
                <span className="block text-[12px] text-gray-500">{t("A buyer who writes while you're closed is told your hours and when you're back: once a day per conversation, and it stays in Needs reply. Needs sending on, above.")}</span>
              </span>
            </label>
            <textarea value={draft.outOfHoursReplyBody} onChange={(e) => change("outOfHoursReplyBody", e.target.value)} rows={4} className={classNames(field, "mt-2")} />
            <MergeHelp fields={OUT_OF_HOURS_FIELDS} />
            <Preview text={draft.outOfHoursReplyBody} values={values} />
          </div>
          <div>
            <label className={label}>{t("At most per day")}</label>
            <input type="number" min={0} max={500} value={draft.autoReplyDailyCap} onChange={(e) => change("autoReplyDailyCap", Number(e.target.value))} className={classNames(field, "w-24")} />
            <p className={help}>{t("Automatic replies of any kind, all conversations together. A brake, not a target.")}</p>
          </div>
          <button type="button" onClick={save} disabled={saving} className={primary}>
            {t("Save")}
          </button>
          {previewConversation && <p className={help}>{t("Previews use the open conversation.")}</p>}
        </div>
      )}
    </Drawer>
  );
}

const emptyTemplate = (): IInboxTemplateRequest => ({ name: "", body: "", type: null, sortOrder: 0 });

function TemplatesTab({
  templates,
  values,
  saving,
  onAdd,
  onUpdate,
  onDelete,
  setNotice,
}: {
  templates: IInboxApiTemplate[];
  values: Record<string, string | null | undefined>;
  saving: boolean;
  onAdd: Props["onAddTemplate"];
  onUpdate: Props["onUpdateTemplate"];
  onDelete: Props["onDeleteTemplate"];
  setNotice: (notice?: IInboxNotice) => void;
}) {
  const { t } = useTranslation("messages");
  const [editingId, setEditingId] = useState<string | "new">();
  const [form, setForm] = useState<IInboxTemplateRequest>(emptyTemplate());

  const startNew = () => {
    setForm({ ...emptyTemplate(), sortOrder: templates.length + 1 });
    setEditingId("new");
  };
  const startEdit = (tpl: IInboxApiTemplate) => {
    setForm({ name: tpl.name, body: tpl.body, type: tpl.type, sortOrder: tpl.sortOrder });
    setEditingId(tpl.id);
  };

  const submit = async () => {
    try {
      if (editingId === "new") await onAdd(form);
      else if (editingId) await onUpdate(editingId, form);
      setEditingId(undefined);
      setNotice({ tone: "green", text: t("Template saved.") });
    } catch (error) {
      setNotice({ tone: "red", text: errorText(error, t("Couldn't save the template – try again.")) });
    }
  };

  const remove = async (id: string) => {
    try {
      await onDelete(id);
      if (editingId === id) setEditingId(undefined);
    } catch (error) {
      setNotice({ tone: "red", text: errorText(error, t("Couldn't remove the template – try again.")) });
    }
  };

  return (
    <div className="space-y-3">
      {templates.length === 0 && !editingId && <p className="text-[13px] text-gray-500">{t("No templates yet. They show under Templates in the reply box.")}</p>}
      <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
        {templates.map((tpl) => (
          <li key={tpl.id} className="flex items-start gap-3 px-3 py-2">
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-medium text-gray-900">
                {tpl.name}
                {tpl.type && <span className="ml-2 rounded-full bg-gray-100 px-1.5 text-[11px] font-normal text-gray-600">{t(TYPE_LABELS[tpl.type])}</span>}
              </p>
              <p className="truncate text-[12px] text-gray-500">{tpl.body}</p>
            </div>
            <button type="button" onClick={() => startEdit(tpl)} className="text-[12px] text-primary-700 hover:underline">
              {t("Edit")}
            </button>
            <button type="button" onClick={() => remove(tpl.id)} disabled={saving} className="text-[12px] text-red-700 hover:underline disabled:opacity-60">
              {t("Remove")}
            </button>
          </li>
        ))}
      </ul>

      {editingId ? (
        <div className="space-y-3 rounded-lg border border-primary-200 bg-primary-50/40 p-3">
          <div className="flex gap-3">
            <div className="flex-1">
              <label className={label}>{t("Name")}</label>
              <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={field} />
            </div>
            <div>
              <label className={label}>{t("For")}</label>
              <select value={form.type ?? ""} onChange={(e) => setForm((f) => ({ ...f, type: (e.target.value || null) as InboxType | null }))} className={classNames(field, "w-44")}>
                <option value="">{t("Any conversation")}</option>
                {(Object.keys(TYPE_LABELS) as InboxType[]).map((type) => (
                  <option key={type} value={type}>
                    {t(TYPE_LABELS[type])}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className={label}>{t("Text")}</label>
            <textarea value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} rows={5} className={field} />
            <MergeHelp />
            {form.body && <Preview text={form.body} values={values} />}
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={submit} disabled={saving || !form.name.trim() || !form.body.trim()} className={primary}>
              {editingId === "new" ? t("Add template") : t("Save template")}
            </button>
            <button type="button" onClick={() => setEditingId(undefined)} className={secondary}>
              {t("Cancel")}
            </button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={startNew} className={secondary}>
          {t("New template")}
        </button>
      )}
    </div>
  );
}
